import { observable } from "mobx";
import {
  ArrayNotEmpty,
  IsEmail,
  IsNotEmpty,
  ValidateNested,
  ValidateIf
} from "class-validator";
import { classToPlain, plainToClass } from "class-transformer";
import locale from "@bonliva-crm/locale";
import { createObjectID, omit } from "@bonliva-crm/common";
import {
  CustomerContractAgreementType,
  CustomerContractCompany,
  CustomerContractContactPersonInput as ICustomerContractContactPersonInput,
  CustomerContractEditorDataFragment,
  CustomerContractFormInput as ICustomerContractFormInput,
  CustomerRefInput
} from "@graphql";

export class CustomerContractContactPersonInput
  implements ICustomerContractContactPersonInput
{
  @observable
  _id = createObjectID();

  @observable
  title: string = "";

  @IsEmail({}, { message: locale.Email })
  @observable
  email: string = "";

  @IsNotEmpty({ message: locale.Name })
  @observable
  name: string = "";

  // @IsNotEmpty({ message: locale.Phone })
  @observable
  phone: string = "";
}

export class CustomerContractFormInput implements ICustomerContractFormInput {
  static CreateFromFragment(data: CustomerContractEditorDataFragment) {
    const item = plainToClass(CustomerContractFormInput, data, {
      excludePrefixes: ["__", "orgCustomerRefs"]
    });

    item.orgCustomers = data.orgCustomerRefs;

    return item;
  }

  getPlain(): ICustomerContractFormInput {
    const plain = classToPlain(this, { excludePrefixes: ["__"] });

    // isNationalProcurement is read-only display state, not part of the input.
    return omit(plain, "_id", "isNationalProcurement") as any;
  }

  @observable
  company: CustomerContractCompany = CustomerContractCompany.Bonliva_AB;

  @ValidateIf((o) => o.company === CustomerContractCompany.Bonliva_Care)
  @IsNotEmpty({
    message: `${locale.fieldIsMandatory(
      locale.customerContractEdit.dispenseDeadline
    )} [1-52]`
  })
  @observable
  dispenseDeadline: number = null;

  @IsNotEmpty({ message: locale.customerContractEdit.dateEnd })
  @observable
  contractEnd: Date = null;

  @observable
  introductionInfo: string = "";

  @IsNotEmpty({ message: locale.customerContractEdit.contractName })
  @observable
  contractName: string = "";

  // @IsNotEmpty({ message: locale.customerContractEdit.contractNumber })
  @observable
  contractNumber: string = "";

  @observable
  avtalsnummer: string = "";

  @observable
  avropsnummer: string = "";

  @observable
  extraInvoiceLine: string = "";

  @observable
  useNationalProcurementInvoicing: boolean = false;

  // Read-only display state (drives the NP-invoicing checkbox disabled state).
  // Stripped from getPlain() — not part of CustomerContractFormInput.
  @observable
  isNationalProcurement: boolean = false;

  @observable
  contractPriceAdjustmentDate: Date = null;

  @ValidateIf((o) => o.contractPriceAdjustmentDate)
  @IsNotEmpty({ message: locale.customerContractEdit.priceAdjustmentDateDeadline })
  @observable
  contractPriceAdjustmentDateDeadline: Date = null;

  @observable
  contractProlongation: Date = null;

  @ValidateIf((o) => o.contractProlongation)
  @IsNotEmpty({ message: locale.customerContractEdit.prolongationDateDeadline })
  @observable
  contractProlongationDeadline: Date = null;

  @IsNotEmpty({ message: locale.customerContractEdit.dateStart })
  @observable
  contractStart: Date = null;

  @observable
  currentContractEnd: Date = null;

  @observable
  estimatedContractValue: number = null;

  @ArrayNotEmpty({ message: locale.customerContractEdit.customers })
  @observable
  orgCustomers: CustomerRefInput[] = [];

  @ArrayNotEmpty({ message: locale.Profession })
  @observable
  professions: string[] = [];

  @IsNotEmpty({ message: locale.customerContractEdit.agreementType })
  @observable
  typeOfAgreement: CustomerContractAgreementType = null;

  @ValidateNested()
  @IsNotEmpty({ message: locale.customerContractEdit.contactPerson })
  @observable
  contactPerson: CustomerContractContactPersonInput[] = [
    new CustomerContractContactPersonInput()
  ];

  @observable
  prolongationComment: string = "";

  @observable
  adjustmentComment: string = "";

  @observable
  overtime: boolean = false;

  @ValidateIf((o) => o.overtime)
  @IsNotEmpty({ message: locale["This field is required"] })
  @observable
  overtimeNote: string = "";

  @observable
  possibleInvoiceForAccommodation: boolean = false;

  @ValidateIf((o) => o.possibleInvoiceForAccommodation)
  @IsNotEmpty({ message: locale["This field is required"] })
  @observable
  possibleInvoiceForAccommodationNote: string = "";

  @observable
  possibleInvoiceForTravel: boolean = false;

  @ValidateIf((o) => o.possibleInvoiceForTravel)
  @IsNotEmpty({ message: locale["This field is required"] })
  @observable
  possibleInvoiceForTravelNote: string = "";

  @observable
  overtimeOption: boolean = false;

  @ValidateIf((o) => o.overtimeOption)
  @IsNotEmpty({ message: locale["This field is required"] })
  @observable
  overtimeOptionOBNote: string = "";

  @observable
  otherOption: boolean = false;

  @ValidateIf((o) => o.otherOption)
  @IsNotEmpty({ message: locale["This field is required"] })
  @observable
  otherOptionNote: string = "";
}
